import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ComplianceBanner from "@/components/ComplianceBanner";
import {
  ArrowRight, ShieldCheck, Truck, Package, Lock, ArrowsClockwise
} from "@phosphor-icons/react";

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/featured-products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="page-enter">
      <ComplianceBanner />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(59,130,246,0.55), transparent)" }}
        />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h1 className="font-display text-[64px] md:text-[92px] lg:text-[128px] leading-[0.85]">
              YOUR<br />
              HEALTH.<br />
              <span className="accent-slash">PRIMED.</span>
            </h1>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/shop" className="btn-prm" data-testid="hero-cta-shop">
                Shop peptides <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/about" className="btn-outline" data-testid="hero-cta-about">
                Why PRMU
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] surface-1 overflow-hidden rounded-2xl glow-pulse">
                <img
                  src="/hero-vials.png"
                  alt="PRMU research peptide vials"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section className="border-b border-white/10 py-6 overflow-hidden">
        <div className="marquee-track flex gap-16 whitespace-nowrap font-mono text-xs uppercase tracking-[0.4em] text-zinc-500">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 shrink-0">
              <span>Free Shipping Over $150</span>
              <span>·</span>
              <span>Fast Delivery</span>
              <span>·</span>
              <span>Discreet Packaging</span>
              <span>·</span>
              <span>Money-Back Guarantee</span>
              <span>·</span>
              <span>2,000+ Labs Trust PRMU</span>
              <span>·</span>
              <span>Human Support · 1 Business Day</span>
              <span>·</span>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          <KPI icon={<ShieldCheck size={22} weight="duotone" />} title="Third-party verified" desc="Independent lab reports attached to every lot." />
          <KPI icon={<Truck size={22} weight="duotone" />} title="Fast, discreet shipping" desc="Ordered by 3pm, dispatched the same day." />
          <KPI icon={<ArrowsClockwise size={22} weight="duotone" />} title="Easy replacements" desc="Damaged in transit? We replace it on us." />
          <KPI icon={<Lock size={22} weight="duotone" />} title="Buyer protection" desc="If a lot misses spec, it never ships." />
        </div>
      </section>

      {/* FEATURED */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <div className="overline mb-3">Best sellers</div>
              <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
                What our <span className="accent-slash">customers</span> reorder.
              </h2>
            </div>
            <Link to="/shop" className="btn-outline shrink-0" data-testid="featured-view-all">
              Shop the range <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* PARTNERS TEASER */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div
          className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(59,130,246,0.5), transparent)" }}
        />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-4">PRMU Partners</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
              Recommend PRMU. <span className="accent-slash">Get paid.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-2xl text-lg">
              Referral link. Your own coupon. Transparent commission ledger. Payouts every two weeks. Approved manually — quality partners only.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/partners" className="btn-prm" data-testid="home-cta-partners">
              Apply to partner <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* PROMISE BLOCK */}
      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <div className="max-w-2xl">
            <div className="overline mb-4">The PRMU promise</div>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.9]">
              What you order<br /><span className="accent-slash">is what you get.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-lg">
              We hold ourselves to a single standard: every lot has to hit spec, or it doesn't go out the door. Simple as that.
            </p>
            <ul className="mt-8 space-y-4 max-w-md">
              {[
                ["Real spec, every time", "We publish the target — and we hit it, batch after batch."],
                ["Ships fast, packs discreet", "Order by 3pm and it's out the same day."],
                ["Reorder-friendly", "Save products, addresses, and past orders in your account."],
                ["Human support", "Real teammates on email within one business day."],
              ].map(([t, d]) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                  <div>
                    <div className="font-display-plain font-semibold">{t}</div>
                    <div className="text-sm text-zinc-500">{d}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex gap-3">
              <Link to="/shop" className="btn-prm">Shop now</Link>
              <Link to="/about" className="btn-outline">Our story</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function KPI({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-blue-400">{icon}</div>
      <div>
        <div className="font-display-plain text-lg font-semibold">{title}</div>
        <div className="text-sm text-zinc-400">{desc}</div>
      </div>
    </div>
  );
}
